import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../utils/const";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TemporaryCustomer = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [rejectProcessing, setRejectProcessing] = useState(false);
  const [cardNo, setCardNo] = useState("");
  const [cardNoError, setCardNoError] = useState("");
  const [schemeType, setSchemeType] = useState(
    () => localStorage.getItem("schemeType") || "mandal"
  );

  const getFullName = (user) => {
    return [user.first_name, user.middle_name, user.last_name]
      .filter(Boolean)
      .join(" ");
  };

  const resolveId = (user) => user?.id ?? user?.user_id ?? user?.mid ?? user?.uid;

  const fetchUsers = () => {
    setLoading(true);
    const endpoint =
      schemeType === "kanchan"
        ? "kanchan/temporary_kanchan_users.php"
        : "mandal/temporary_mandal_users.php"; 

    apiClient
      .post(endpoint)
      .then(({ data }) => {
        // Accept either { status: true, data: { users: [...] } } or { users: [...] }
        const usersArr = Array.isArray(data?.data?.users)
          ? data.data.users
          : Array.isArray(data?.users)
          ? data.users
          : null;

        if (usersArr) {
          setUsers(usersArr);
        } else {
          toast.error(data?.message || "Unexpected response format from server.");
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Network error while fetching users."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeType]);

  useEffect(() => {
    const handleSchemeChange = () => {
      setSchemeType(localStorage.getItem("schemeType") || "mandal");
    };
    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);

  const handleAcceptUserClick = (user) => {
    const id = resolveId(user);
    const name = getFullName(user);
    setSelectedUserId(id);
    setSelectedUserName(name);
    setCardNo(user.card_no || "");
    setCardNoError("");
    setShowConfirmModal(true);
  };

  const handleRejectUserClick = (userId, name) => {
    setSelectedUserId(userId);
    setSelectedUserName(name);
    setShowDeleteModal(true);
  };

  const cancelAcceptUser = () => {
    setShowConfirmModal(false);
    setSelectedUserId(null);
    setSelectedUserName("");
    setCardNo("");
    setCardNoError("");
  };

  const cancelRejectUser = () => {
    setShowDeleteModal(false);
    setSelectedUserId(null);
    setSelectedUserName("");
  };

  const confirmAcceptUser = () => {
    const trimmedCard = cardNo.trim();
    if (!trimmedCard) {
      setCardNoError("Card number is required.");
      return;
    }
    setProcessing(true);
    const endpoint =
      schemeType === "kanchan"
        ? "kanchan/confirmed_kanchan_user.php"
        : "mandal/confirmed_mandal_user.php";

    apiClient
      .post(endpoint, { user_id: selectedUserId, card_no: trimmedCard })
      .then(({ data }) => {
        if (data?.message === "radha") {
          toast.success(`${selectedUserName} has been accepted successfully.`);
          fetchUsers();
        } else {
          toast.error(
            `Failed to accept ${selectedUserName}: ${data?.message || "Unknown error"}`
          );
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Network error while accepting user."
        );
      })
      .finally(() => {
        setProcessing(false);
        cancelAcceptUser();
      });
  };

  const confirmRejectUser = () => {
    setRejectProcessing(true);
    const endpoint =
      schemeType === "kanchan"
        ? "kanchan/reject_kanchan_users.php"
        : "mandal/reject_mandal_users.php";

    apiClient
      .post(endpoint, { id: selectedUserId })
      .then(({ data }) => {
        if (data?.message === "radha") {
          toast.success(`${selectedUserName} has been rejected and removed successfully.`);
          fetchUsers();
        } else {
          toast.error(
            `Failed to reject ${selectedUserName}: ${data?.message || "Unknown error"}`
          );
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Network error while rejecting user."
        );
      })
      .finally(() => {
        setRejectProcessing(false);
        cancelRejectUser();
      });
  };

  // Filter + pagination (stringify phone to avoid .toLowerCase on numbers)
  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((user) => {
      const name = getFullName(user).toLowerCase();
      const phone = String(user.phone ?? "").toLowerCase();
      return name.includes(q) || phone.includes(q);
    });
  }, [users, search]);

  const totalPages = Math.ceil(filteredUsers.length / entriesPerPage) || 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPage;
    return filteredUsers.slice(start, start + entriesPerPage);
  }, [filteredUsers, currentPage, entriesPerPage]);

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container-xxl">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h1
                        className="card-title"
                        style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                      >
                        {schemeType === "kanchan" ? "Kanchan" : "Mandal"} • On-boarding User
                      </h1>
                    </div>
                  </div>
                </div>

                <div className="card-body pt-0">
                  <div
                    className="d-flex justify-content-between align-items-center mb-3"
                    style={{ marginTop: "1rem" }}
                  >
                    <div>
                      <select
                        value={entriesPerPage}
                        onChange={(e) => {
                          setEntriesPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="form-select d-inline-block w-auto"
                      >
                        {[5, 10, 20, 50].map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                      <span className="ms-2">entries per page</span>
                    </div>
                    <input
                      type="text"
                      className="form-control w-auto"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ minWidth: 200 }}
                    />
                  </div>

                  <div className="table-responsive">
                    {loading ? (
                      <div className="text-center p-4">Loading...</div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="text-center p-4">No temporary users found.</div>
                    ) : (
                      <table className="table mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center px-3 py-3 border-0">ID</th>
                            <th className="text-start px-3 py-3 border-0">NAME</th>
                            <th className="text-center px-3 py-3 border-0">PHONE</th>
                            <th className="text-center px-3 py-3 border-0">ACTION</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map((user, idx) => {
                            const id = resolveId(user) ?? idx; // fallback to idx for key safety
                            const name = getFullName(user);
                            return (
                              <tr
                                key={id}
                                className="border-bottom"
                                style={{
                                  borderBottom: "1px solid var(--bs-border-color)",
                                  transition: "all 0.3s ease",
                                  backgroundColor: idx % 2 === 0 ? "transparent" : "var(--bs-gray-50)",
                                }}
                                onMouseEnter={(e) => {
                                  const tr = e.target.closest("tr");
                                  tr.style.backgroundColor = "var(--bs-primary-bg-subtle)";
                                  tr.style.transform = "translateY(-1px)";
                                  tr.style.boxShadow = "0 4px 20px rgba(0,0,0,0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  const tr = e.target.closest("tr");
                                  const idx = Array.from(tr.parentNode.children).indexOf(tr);
                                  tr.style.backgroundColor = idx % 2 === 0 ? "transparent" : "var(--bs-gray-50)";
                                  tr.style.transform = "translateY(0px)";
                                  tr.style.boxShadow = "none";
                                }}
                              >
                                {/* ID Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="badge bg-primary bg-gradient px-2 py-1"
                                    style={{
                                      fontSize: "0.8rem",
                                      fontWeight: "600",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    #{resolveId(user)}
                                  </span>
                                </td>

                                {/* Name Column */}
                                <td className="text-start px-3 py-3 border-0">
                                  <div>
                                    <h6
                                      className="mb-0 fw-semibold"
                                      style={{
                                        fontSize: "0.95rem",
                                        color: "var(--bs-body-color)",
                                      }}
                                    >
                                      {name}
                                    </h6>
                                  </div>
                                </td>

                                {/* Phone Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="fw-medium"
                                    style={{
                                      fontSize: "0.9rem",
                                      color: "var(--bs-secondary-color)",
                                    }}
                                  >
                                    {user.phone ?? "—"}
                                  </span>
                                </td>

                                {/* Actions Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <div className="btn-group" role="group">
                                    <button
                                      type="button"
                                      className="btn btn-outline-success btn-sm me-1"
                                      title="Accept User"
                                      onClick={() => handleAcceptUserClick(user)}
                                      style={{
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        transition: "all 0.2s ease",
                                        border: "1px solid #28a745",
                                        color: "#28a745",
                                        backgroundColor: "transparent",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#28a745";
                                        e.currentTarget.style.color = "white";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(40, 167, 69, 0.3)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                        e.currentTarget.style.color = "#28a745";
                                        e.currentTarget.style.transform = "translateY(0px)";
                                        e.currentTarget.style.boxShadow = "none";
                                      }}
                                    >
                                      <i className="fas fa-check me-1" style={{ fontSize: "0.8rem" }}></i>
                                      Accept
                                    </button>

                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      title="Reject User"
                                      onClick={() => handleRejectUserClick(resolveId(user), name)}
                                      style={{
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        transition: "all 0.2s ease",
                                        border: "1px solid #dc3545",
                                        color: "#dc3545",
                                        backgroundColor: "transparent",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#dc3545";
                                        e.currentTarget.style.color = "white";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.3)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                        e.currentTarget.style.color = "#dc3545";
                                        e.currentTarget.style.transform = "translateY(0px)";
                                        e.currentTarget.style.boxShadow = "none";
                                      }}
                                    >
                                      <i className="fas fa-times me-1" style={{ fontSize: "0.8rem" }}></i>
                                      Reject
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {!loading && filteredUsers.length > 0 && (
                    <div className="d-flex justify-content-center align-items-center mt-3">
                      <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Prev
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="btn btn-outline-secondary btn-sm ms-2"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showConfirmModal && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                maxWidth: "400px",
                width: "90%",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              }}
            >
              <div
                className="header"
                style={{
                  borderBottom: "1px solid #4B2C3B",
                  paddingBottom: "16px",
                  marginBottom: "16px",
                }}
              >
                <h5
                  className="title"
                  style={{
                    margin: 0,
                    color: "white",
                    backgroundColor: "#4B2C3B",
                    padding: "8px",
                  }}
                >
                  Confirm Action
                </h5>
              </div>
              <div className="modal-body" style={{ marginBottom: "20px" }}>
                <p style={{ margin: 0, color: "#666", lineHeight: 1.5 }}>
                  Do you want to accept <strong>{selectedUserName}</strong> as an Member?
                </p>
                <div style={{ marginTop: "12px" }}>
                  <label
                    htmlFor="cardNoInput"
                    style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "6px" }}
                  >
                    Card Number
                  </label>
                  <input
                    id="cardNoInput"
                    type="text"
                    value={cardNo}
                    onChange={(e) => {
                      setCardNo(e.target.value);
                      if (cardNoError) setCardNoError("");
                    }}
                    placeholder="Enter card number"
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "4px",
                      border: cardNoError ? "1px solid #dc3545" : "1px solid #ced4da",
                      fontSize: "0.95rem",
                    }}
                  />
                  {cardNoError && (
                    <span style={{ color: "#dc3545", fontSize: "0.8rem", marginTop: "4px", display: "block" }}>
                      {cardNoError}
                    </span>
                  )}
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
              >
                <button
                  onClick={cancelAcceptUser}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    color: "#6c757d",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAcceptUser}
                  disabled={processing}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #4B2C3B",
                    borderRadius: "4px",
                    backgroundColor: processing ? "#aaa" : "#4B2C3B",
                    color: "white",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  {processing ? "Accepting..." : "Accept"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "0",
                maxWidth: "400px",
                width: "90%",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "20px 24px",
                  borderBottom: "1px solid #e9ecef",
                  backgroundColor: "transparent",
                }}
              >
                <h5 style={{ margin: 0, fontWeight: "600", color: "#333" }}>
                  Delete User
                </h5>
                <button
                  onClick={cancelRejectUser}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "18px",
                    cursor: "pointer",
                    color: "#6c757d",
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body" style={{ padding: "24px" }}>
                <p style={{ margin: "0 0 16px 0", color: "#333", lineHeight: 1.5 }}>
                  Are you sure you want to remove <strong>{selectedUserName}</strong>?
                </p>
                <p style={{ margin: 0, color: "#dc3545", fontSize: "14px", lineHeight: 1.4 }}>
                  This action cannot be undone. The user will be permanently removed from the database.
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ 
                  padding: "16px 24px 24px 24px",
                  display: "flex", 
                  justifyContent: "flex-end" 
                }}
              >
                <button
                  onClick={confirmRejectUser}
                  disabled={rejectProcessing}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: "4px",
                    backgroundColor: rejectProcessing ? "#aaa" : "#dc3545",
                    color: "white",
                    cursor: rejectProcessing ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <i className="fas fa-trash" style={{ fontSize: "12px" }}></i>
                  {rejectProcessing ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemporaryCustomer;
